import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Play, Trophy, Clock, User, XCircle, Volume2, VolumeX } from 'lucide-react';

// Data configuration for items in the circle
const itemConfigs = [
  { key: 'juice', name: 'Juice', count: 5, emoji: '🥤' },
  { key: 'cap', name: 'Cap', count: 4, emoji: '🧢' },
  { key: 'train', name: 'Train', count: 2, emoji: '🚂' },
  { key: 'dice', name: 'Dice', count: 3, emoji: '🎲' },
  { key: 'coconut', name: 'Coconut', count: 6, emoji: '🥥' },
  { key: 'bluecup', name: 'Cup', count: 5, emoji: '☕' }
];

// Helper to shuffle arrays
const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

// Generates randomized 2D positional coordinates distributed safely inside the circle boundary
const generateCirclePositions = () => {
  const fullItemsList = itemConfigs.flatMap((config) =>
    Array(config.count).fill(config)
  );

  const shuffledItems = shuffleArray(fullItemsList);
  const minDistance = 38; // Minimum pixel distance between item centers to prevent overlap
  const placedItems = [];

  const centerX = 200;
  const centerY = 200;
  const maxRadius = 135; // Keeps items well within the r=180 circle boundary

  shuffledItems.forEach((item) => {
    let positionFound = false;
    let attempts = 0;
    let x = centerX;
    let y = centerY;

    while (!positionFound && attempts < 200) {
      attempts++;

      // Randomize radius and angle to fill the interior of the circle evenly
      const r = Math.sqrt(Math.random()) * maxRadius;
      const angle = Math.random() * 2 * Math.PI;

      const testX = Math.round(centerX + r * Math.cos(angle));
      const testY = Math.round(centerY + r * Math.sin(angle));

      // Check collision against all previously placed items
      const hasOverlap = placedItems.some((placed) => {
        const dx = placed.x - testX;
        const dy = placed.y - testY;
        return Math.hypot(dx, dy) < minDistance;
      });

      if (!hasOverlap) {
        x = testX;
        y = testY;
        positionFound = true;
      }
    }

    placedItems.push({ ...item, x, y });
  });

  return placedItems;
};

export default function CountAndWriteGame() {
  const [playerName, setPlayerName] = useState('');
  const [activePlayer, setActivePlayer] = useState('Little Learner');
  const [isMuted, setIsMuted] = useState(false);

  // Circle Layout State & Answers
  const [positionedItems, setPositionedItems] = useState(() => generateCirclePositions());
  const [userAnswers, setUserAnswers] = useState({});

  // Game Progress States
  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [gameResult, setGameResult] = useState({ ok: false, message: '' });

  // Voice synthesis helper using Web Speech API
  const speakText = (text) => {
    if (isMuted) return;
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setPlayerName(value);
    setActivePlayer(value.trim() ? value.trim() : 'Little Learner');
  };

  const toggleMute = () => {
    if (!isMuted && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsMuted(!isMuted);
  };

  // Announce instructions on initial mount
  useEffect(() => {
    speakText("Welcome! Count each item inside the circle and write your answers in the input boxes below.");
  }, []);

  // Timer Effect
  useEffect(() => {
    let interval = null;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  const handleInputChange = (itemKey, value) => {
    const numericValue = value === '' ? '' : parseInt(value, 10);
    setUserAnswers((prev) => ({
      ...prev,
      [itemKey]: numericValue
    }));
  };

  const handleResetCurrent = () => {
    setUserAnswers({});
    setTimer(0);
    setIsTimerActive(true);
    speakText("Answers cleared. Try again!");
  };

  const handlePlayAgainAndShuffle = () => {
    // If the input field has a name, use it; otherwise default smoothly to "Little Learner" without a prompt
    const currentName = playerName.trim() || 'Little Learner';

    setActivePlayer(currentName);
    setPositionedItems(generateCirclePositions());
    setUserAnswers({});
    setTimer(0);
    setIsTimerActive(true);

    speakText(`Welcome ${currentName}! Count the items in the circle and write the correct amounts.`);
  };

  const handleSubmit = () => {
    setIsTimerActive(false);

    let isAllCorrect = true;
    itemConfigs.forEach((config) => {
      const answer = userAnswers[config.key];
      if (answer !== config.count) {
        isAllCorrect = false;
      }
    });

    const resultMsg = isAllCorrect
      ? `Fantastic job, ${activePlayer}! You counted every item perfectly!`
      : `Some counts are not quite right, ${activePlayer}. Double-check the circle and try again!`;

    setGameResult({ ok: isAllCorrect, message: resultMsg });
    setShowModal(true);
    speakText(resultMsg);
  };

  const closeModal = () => {
    setShowModal(false);
    handlePlayAgainAndShuffle();
  };

  const speakInstructionManually = () => {
    speakText(`Hello ${activePlayer}! Count each type of object scattered inside the circle, then type the counts in the input boxes.`);
  };

  return (
    <div style={styles.container}>
      <main style={styles.main}>
        {/* Controls Toolbar */}
        <section style={styles.toolbar}>
          {/* Player Input */}
          <div style={styles.inputGroup}>
            <User style={{ width: 18, height: 18, color: '#3B82F6' }} />
            <input
              type="text"
              placeholder="Enter Name"
              value={playerName}
              onChange={handleNameInputChange}
              style={styles.input}
            />
          </div>

          {/* Audio/Voice Controls */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={speakInstructionManually}
              style={{
                ...styles.voiceBtn,
                opacity: isMuted ? 0.6 : 1
              }}
              title="Listen to Instructions"
            >
              <Volume2 style={{ width: 18, height: 18 }} /> Listen
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleMute}
              style={{
                ...styles.muteBtn,
                background: isMuted ? '#FEE2E2' : '#F1F5F9',
                color: isMuted ? '#EF4444' : '#64748B'
              }}
              title={isMuted ? "Unmute Voice" : "Mute Voice"}
            >
              {isMuted ? <VolumeX style={{ width: 18, height: 18 }} /> : <Volume2 style={{ width: 18, height: 18 }} />}
            </motion.button>
          </div>

          {/* Shuffle & Replay */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handlePlayAgainAndShuffle}
            style={styles.playBtn}
          >
            <Play style={{ width: 16, height: 16 }} /> Play Again & Shuffle
          </motion.button>

          {/* Timer Display */}
          <div style={styles.timerGroup}>
            <label style={styles.timerLabel}>
              <input
                type="checkbox"
                checked={isTimerActive}
                onChange={(e) => setIsTimerActive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Timer
            </label>
            <div style={styles.timerText}>
              <Clock style={{ width: 16, height: 16 }} />
              <span>{timer}s</span>
            </div>
          </div>
        </section>

        {/* Main Stage */}
        <section style={styles.gameCardWhite}>
          <div style={styles.gameHeader}>
            <h2 style={styles.gameTitle}>
              <Sparkles style={{ width: 22, height: 22, color: '#F59E0B' }} /> Count and Write Game
            </h2>
            <span style={styles.playerBadge}>
              Player: {activePlayer}
            </span>
          </div>

          {/* Circle Visualization Stage */}
          <div style={styles.circleStage}>
            <svg width="400" height="400" viewBox="0 0 400 400" style={styles.svgLayer}>
              <defs>
                <linearGradient id="circleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#F5D8F0', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#FFE4ED', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#F5D0E8', stopOpacity: 1 }} />
                </linearGradient>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.15" />
                </filter>
              </defs>
              <circle cx="200" cy="200" r="180" fill="url(#circleGrad)" filter="url(#shadow)" stroke="#E8C8E0" strokeWidth="3" />
              <circle cx="200" cy="200" r="175" fill="none" stroke="#F5E8F0" strokeWidth="2" strokeDasharray="5,8" opacity="0.6" />
            </svg>

            <div style={styles.itemPlacementArea}>
              {positionedItems.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: idx * 0.02 }}
                  style={{
                    ...styles.objectItem,
                    left: `${item.x - 20}px`,
                    top: `${item.y - 20}px`
                  }}
                  title={item.name}
                >
                  {item.emoji}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Form Inputs Grid */}
          <div style={styles.inputsGrid}>
            {itemConfigs.map((config) => (
              <div key={config.key} style={styles.inputCard}>
                <div style={styles.cardEmoji}>{config.emoji}</div>
                <input
                  type="number"
                  min="0"
                  max="20"
                  placeholder="?"
                  value={userAnswers[config.key] ?? ''}
                  onChange={(e) => handleInputChange(config.key, e.target.value)}
                  style={styles.numberInput}
                />
                <span style={styles.cardLabel}>{config.name}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={styles.actionBar}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              style={styles.submitBtn}
            >
              Submit Answer
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleResetCurrent}
              style={styles.resetBtn}
            >
              <RefreshCw style={{ width: 18, height: 18 }} /> Clear Choices
            </motion.button>
          </div>
        </section>
      </main>

      {/* Result Modal */}
      <AnimatePresence>
        {showModal && (
          <div style={styles.modalOverlay}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              style={styles.modalCard}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                {gameResult.ok ? (
                  <div style={styles.iconCircleSuccess}>
                    <Trophy style={{ width: 44, height: 44, color: '#10B981' }} />
                  </div>
                ) : (
                  <div style={styles.iconCircleFail}>
                    <XCircle style={{ width: 44, height: 44, color: '#EF4444' }} />
                  </div>
                )}
              </div>

              {gameResult.ok && (
                <div style={{ color: '#F59E0B', fontSize: '1.4rem', marginBottom: 8, letterSpacing: '2px' }}>
                  ⭐⭐⭐⭐⭐
                </div>
              )}

              <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1E293B', marginBottom: 8 }}>
                {gameResult.ok ? `Great job, ${activePlayer}!` : `Check again, ${activePlayer}!`}
              </h3>

              <p style={{ color: '#64748B', fontSize: '0.95rem', marginBottom: 16, lineHeight: 1.4 }}>
                {gameResult.message}
              </p>

              <div style={styles.modalTimerBox}>
                <Clock style={{ width: 16, height: 16, color: '#2563EB' }} /> Time Spent: <strong>{timer}s</strong>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={closeModal}
                style={styles.modalCloseBtn}
              >
                Shuffle & Play Again
              </motion.button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#F1F5F9',
    color: '#0F172A',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    padding: '32px 16px',
    boxSizing: 'border-box'
  },
  main: {
    maxWidth: '850px',
    margin: '0 auto'
  },
  toolbar: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '16px',
    padding: '12px 20px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '8px 12px',
    border: '1px solid #CBD5E1'
  },
  input: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#0F172A',
    fontSize: '0.9rem',
    fontWeight: '500',
    width: '120px'
  },
  voiceBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    color: '#2563EB',
    padding: '8px 14px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.85rem',
    cursor: 'pointer'
  },
  muteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  playBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.9rem',
    cursor: 'pointer'
  },
  timerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '8px 14px',
    border: '1px solid #CBD5E1'
  },
  timerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#475569',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  timerText: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#2563EB',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    fontSize: '0.95rem'
  },
  gameCardWhite: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: '28px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)'
  },
  gameHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #F1F5F9',
    paddingBottom: '16px',
    marginBottom: '20px'
  },
  gameTitle: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  playerBadge: {
    fontSize: '0.8rem',
    fontWeight: '600',
    background: '#EFF6FF',
    color: '#2563EB',
    border: '1px solid #BFDBFE',
    padding: '4px 12px',
    borderRadius: '20px'
  },
  circleStage: {
    position: 'relative',
    width: '400px',
    height: '400px',
    margin: '0 auto 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  svgLayer: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  itemPlacementArea: {
    position: 'absolute',
    width: '400px',
    height: '400px'
  },
  objectItem: {
    position: 'absolute',
    fontSize: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '40px',
    height: '40px',
    userSelect: 'none'
  },
  inputsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  inputCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    padding: '12px 16px',
    width: '90px'
  },
  cardEmoji: {
    fontSize: '2rem',
    marginBottom: '6px'
  },
  numberInput: {
    width: '60px',
    textAlign: 'center',
    padding: '8px',
    border: '2px solid #2563EB',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    outline: 'none',
    color: '#0F172A'
  },
  cardLabel: {
    fontSize: '0.75rem',
    color: '#64748B',
    marginTop: '6px',
    fontWeight: '600'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '2px solid #F1F5F9'
  },
  submitBtn: {
    padding: '12px 28px',
    background: '#059669',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: '1rem',
    cursor: 'pointer'
  },
  resetBtn: {
    padding: '12px 22px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    borderRadius: '12px',
    color: '#475569',
    fontWeight: '600',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)'
  },
  modalCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: '28px',
    maxWidth: '360px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
  },
  iconCircleSuccess: {
    padding: '12px',
    background: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: '50%'
  },
  iconCircleFail: {
    padding: '12px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '50%'
  },
  modalTimerBox: {
    background: '#F8FAFC',
    borderRadius: '8px',
    padding: '8px 16px',
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#334155',
    border: '1px solid #E2E8F0',
    fontSize: '0.9rem'
  },
  modalCloseBtn: {
    width: '100%',
    padding: '12px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    fontWeight: '700',
    borderRadius: '10px',
    cursor: 'pointer'
  }
};